import ContextualBar from '@/components/ContextualBar';
import Layout from '@/components/layout';
import { PATH } from '@/constants/path';
import { deepCompareObjectEqual, deepObjectEqual } from '@/helpers';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useToast from '@/hooks/useToast';
import { apiCaller } from '@/redux/query';
import appbarSlice, { isShowContextualBarSelector } from '@/redux/slice/appbar.slice';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import navigateSlice from '@/redux/slice/navigate';
import settingsSlice, { dataSettingsBackupSelector, dataSettingsSelector } from '@/redux/slice/settings.slice';
import { Tabs } from '@shopify/polaris';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DateAndTime from './components/DateAndTime';
import DisplayAndPosition from './components/DisplayAndPosition';
import GeolocationAndValidity from './components/GeolocationAndValidity';

const GeneralSettings = () => {
  useCustomUserIdClarity('General settings');
  const dataSettings = useSelector(dataSettingsSelector);
  const dataSettingsBackup = useSelector(dataSettingsBackupSelector);
  const isShowContextualBar = useSelector(isShowContextualBarSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const [updateGeneralSetting, { isLoading: isLoadingUpdateGeneralSetting }] = apiCaller.useUpdateGeneralSettingMutation();
  const { show } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const newDataSettings = { ...dataSettings, show_on_pages: dataSettings.show_on_pages.split(',').sort().join(',') };
    const newDataSettingsBackup = {
      ...dataSettingsBackup,
      show_on_pages: dataSettingsBackup.show_on_pages.split(',').sort().join(','),
    };
    const check = !deepCompareObjectEqual(newDataSettings, newDataSettingsBackup);
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: check }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dataSettingsBackup), JSON.stringify(dataSettings)]);

  const tabs = useMemo(
    () => [
      {
        id: PATH.GENERAL_SETTINGS.pathname,
        path: PATH.GENERAL_SETTINGS,
        content: 'Date and time',
      },
      {
        id: PATH.GEOLOCATION_VALIDITY.pathname,
        path: PATH.GEOLOCATION_VALIDITY,
        content: 'Geolocation and validity',
      },
      {
        id: PATH.DISPLAY_POSITION.pathname,
        path: PATH.DISPLAY_POSITION,
        content: 'Display and position',
      },
    ],
    [],
  );

  const handleTabChange = (selectedTabIndex: number) => {
    if (!deepObjectEqual(dataSettings, dataSettingsBackup)) {
      dispatch(navigateSlice.actions.handleNavigate(tabs[selectedTabIndex].path));
      return;
    }
    navigate(tabs[selectedTabIndex].path);
  };

  const currentPath = window.location.pathname;
  const selected = useMemo(() => {
    const tabIndex = tabs.findIndex((tab) => tab.id === currentPath);
    if (tabIndex !== -1) {
      return tabIndex;
    }
    return 0;
  }, [currentPath, tabs]);

  const content = useMemo(() => {
    switch (selected) {
      case 2:
        return <DisplayAndPosition />;
      case 1:
        return <GeolocationAndValidity />;
      default:
        return <DateAndTime />;
    }
  }, [selected]);

  const handleSave = async () => {
    if (isSkip) return;
    try {
      const res = await updateGeneralSetting(dataSettings).unwrap();
      if (res.status === 'success') {
        show('Saved');
      }
    } catch (error) {}
  };

  const handleDiscard = () => {
    if (isShowContextualBar && dataSettingsBackup) {
      dispatch(settingsSlice.actions.handleDataSettings(dataSettingsBackup));
    }
  };

  return (
    <Layout
      title="General Settings"
      fullWidth={false}
      headers={
        <ContextualBar
          isVisible={isShowContextualBar}
          saveAction={handleSave}
          loadingSave={isLoadingUpdateGeneralSetting}
          isDisabled={isLoadingUpdateGeneralSetting}
          isDisabledDiscard={isLoadingUpdateGeneralSetting}
          discardAction={handleDiscard}
        />
      }
    >
      <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} fitted />
      <div>{content}</div>
    </Layout>
  );
};

export default GeneralSettings;
